import styles from './AgentOpsQueuePage.module.css';

/**
 * Shared status pill for AgentPayout rows.
 *
 * Extracted from AgentOpsQueuePage (Phase 7d) to AgentStatusBadge
 * (Phase 7e) so both the ops triage queue AND the per-agent
 * history page render statuses identically. Source of truth for
 * the status → color/label mapping; if AgentPayoutStatus enum
 * values change (Phase 1a defines the set), update the labels
 * table here and both consumers get the fix.
 *
 * Visual contract:
 *   - clawback_pending / permanently_failed → red (badgeErr)
 *   - failed / pending / pending_stripe_setup → yellow (badgeWarn)
 *   - transferred → green (badgeOk)
 *   - reversed / absorbed_clawback → gray (badgeMuted)
 *   - unknown status → gray fallback with the raw status string
 *
 * When `manualReview=true`, a secondary "Manual Review" badge
 * stacks below the primary status. Both badges render
 * independently of the row's status because manual_review_required
 * is an orthogonal attention signal (Phase 3d field, Phase 6b /
 * 3e writer paths).
 *
 * Style classes live in AgentOpsQueuePage.module.css and are
 * shared by import , keeps the CSS namespaced to a single module
 * without a separate stylesheet file for the badge.
 */

interface Props {
  status: string;
  manualReview?: boolean;
}

interface BadgeInfo {
  label: string;
  cls: string;
}

export function AgentStatusBadge({ status, manualReview = false }: Props) {
  // CSS-module class lookups fall back to `badgeMuted` to keep the
  // helper honest about which classes may technically be undefined
  // at the TS layer (CSS modules don't propagate names statically).
  // Same defensive pattern Phase 7d shipped in the inline helper
  // this replaces.
  const fallback = styles.badgeMuted ?? '';
  const labels: Record<string, BadgeInfo> = {
    clawback_pending: { label: 'Clawback Pending', cls: styles.badgeErr ?? fallback },
    permanently_failed: { label: 'Permanently Failed', cls: styles.badgeErr ?? fallback },
    failed: { label: 'Failed', cls: styles.badgeWarn ?? fallback },
    pending: { label: 'Pending', cls: styles.badgeWarn ?? fallback },
    pending_stripe_setup: { label: 'Pending Stripe Setup', cls: styles.badgeWarn ?? fallback },
    transferred: { label: 'Transferred', cls: styles.badgeOk ?? fallback },
    reversed: { label: 'Reversed', cls: fallback },
    absorbed_clawback: { label: 'Absorbed Clawback', cls: fallback },
  };
  const info = labels[status] ?? { label: status, cls: fallback };
  return (
    <div className={styles.badgeStack}>
      <span className={info.cls}>{info.label}</span>
      {manualReview && (
        <span className={styles.badgeReview}>Manual Review</span>
      )}
    </div>
  );
}
